import axios, { type AxiosError } from 'axios';
import { type LocationQuery } from 'vue-router';
import { type Mutable } from 'type-fest';
import {
    HotelFilters,
    type HotelFiltersData,
    type HotelPriceObject,
    HotelSortBy,
    StarFilter,
} from './HotelModuleTypes';
import HotelRoomType from './HotelRoomType';
import { type HotelSearchForm } from '@search/hotel/HotelSearchForm';
import ItineraryModule from '@itinerary/ItineraryModule';
import {
    type FloatingReserveResponse,
    HotelApiApiHotelCityNameHotelNameStaticGet,
    type HotelCommentsApiApiHotelHotelIdAddCommentPostRequest,
    HotelApiApiHotelHotelIdStaticGet,
    type HotelBlockDetailDto,
    type HotelResponseRoomPrice,
    HotelRoomApiApiHotelRoomPriceListingsRoomIdGet,
    HotelSearchApiApiHotelsNearByHotelsGet,
    type HotelSearchApiApiHotelsNearByHotelsGetRequest,
    RatingFactorApiApiRatingFactorGet,
    type RatingFactorDto,
    ReserveApiApiReserveFloatingBillCodeMacGet,
    type ReserveApiApiReserveFloatingBillCodeMacGetRequest,
    ReserveApiApiReserveReserveHotelSuggestionBillCodeGet,
    ReserveItemStatus,
    HotelApiApiHotelCityNameHotelNameRecommendationsGet,
    type HotelApiApiHotelCityNameHotelNameRecommendationsGetRequest,
} from '@clients/HotelApi';
import HotelPackageSet from '@itinerary/hotel/HotelPackageSet';
import type HotelPriceClass from '@itinerary/hotel/HotelPriceClass';
import {
    CheckboxFilter,
    type EmptyListReason,
    type Filter,
    type IdTitle,
    PriceFilter,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import HotelPackage from '@itinerary/hotel/HotelPackage';
import { type SrpEventStatus } from '@store/analytics';
import { PaxType } from '@utils/types';
import { ReserveType, ViewNotFoundError } from '@utils/itinerary';
import {
    type HotelLocation,
    type HotelPathData,
    type HotelUrlParams,
} from '@utils/hotel';
import { InformationRegisterStatus } from '@store/invoice';
import { Gender, Nationality } from '@store/form';
import { type HotelPackageLoaderConfig } from '@itinerary/hotel/types';
import { FormDataSerializer } from '#imports';

export default class HotelModule extends ItineraryModule<
    HotelPriceClass,
    HotelFiltersData,
    HotelLocation,
    HotelPackage,
    HotelPackageSet
> {
    //#region state
    override sortBy: HotelSortBy = HotelSortBy.HigherScore;
    nearbyHotels: HotelPackage[] = [];
    hotelCouponBlocking: HotelBlockDetailDto[] = [];
    hotelCouponBlockingLoading = true;
    isLoadingHotelInfo = false;
    isLoadingNearbyHotels = true;
    floatingInfo: FloatingReserveResponse | null = null;
    currentSelectedPackageId: string | null = null;
    ssrHotelPackage: HotelPackage | null = null;
    //#endregion

    constructor(
        public descriptionTemplate = 'رزرو هتل در (اسم شهر) با مناسب‌ترین قیمت، عکس و مشخصات هتل‌های (اسم شهر) در مِستربلیط همراه با مشاوره آنلاین و تلفنی: 02161169000'
    ) {
        super();
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
    }

    override loadLaterals() {
        return Promise.resolve();
    }

    get hotelPricesLoading() {
        return !!this.currentPackageSet?.loadingPrices;
    }

    override get reserveInfo() {
        return this.hotelForm.reserveInfo;
    }

    override get metaTagTitle(): string {
        const { hotelForm } = this;
        const { userDestination } = hotelForm;
        if (userDestination?.type === 'city') {
            return `رزرو هتل ${userDestination.title} با بهترین قیمت تا ۷۹٪ تخفیف - مِستربلیط`;
        }
        const destinationTitle = userDestination?.title?.replace('هتل', '');
        const cityName = userDestination?.subtitle
            ? ` ${userDestination.subtitle}`
            : '';
        return `رزرو هتل ${destinationTitle}${cityName} با بهترین قیمت تا ۷۹٪ تخفیف - مِستربلیط`;
    }

    override get defaultSortBy(): HotelSortBy {
        return HotelSortBy.MrbilitOffer;
    }

    override get emptyListReason(): EmptyListReason {
        return {
            image: `/img/no-result/${this.reserveInfo.type}.svg`,
            title: 'در تاریخ و مقصد مورد نظر',
            notFoundText: `${this.reserveInfo.nameE} موجود نیست!`,
        };
    }

    override get dateTitle(): string {
        const { fromDate, toDate } = this.hotelForm;
        const start = fromDate || new Date();
        const end = toDate || dayConfig.gregory(start).add(1, 'day').toDate();
        const startDay = dayConfig.gregory(start).startOf('day');
        const duration = dayConfig.gregory(end).diff(startDay, 'day');
        return `${formatDateRange(start, end)}${
            duration ? ` (${duration} شب)` : ''
        }`; // never happens 0 nights, but to making sure 😅
    }

    get floatingState() {
        return this.floatingInfo?.reserveItems
            ? this.floatingInfo?.reserveItems[0].status
            : ReserveItemStatus.Waiting;
    }

    get hotelCouponBlockHotels() {
        return this.hotelCouponBlocking.map(
            block =>
                new HotelPackage({
                    hotel: block.hotel,
                    cityName:
                        block.hotel?.englishCityName?.toLowerCase() ||
                        undefined,
                    totalPrice: block.salePrice || undefined,
                    payable:
                        (block.salePrice ?? 0) - (block.discountAmount ?? 0),
                })
        );
    }

    ratingFactors: RatingFactorDto[] = [];

    async loadRatingFactors() {
        if (this.ratingFactors.length) {
            return;
        }
        try {
            const { data } = await RatingFactorApiApiRatingFactorGet();
            this.ratingFactors = data;
        } catch (e) {
            reportException(e);
        }
    }

    async addComment(
        request: Mutable<HotelCommentsApiApiHotelHotelIdAddCommentPostRequest>,
        rating: number
    ) {
        request.commentRatings = this.ratingFactors.map(factor => ({
            ratingFactorId: factor.id,
            rating,
        }));
        await axios.post(
            `${useBaseUrls().hotel}/api/hotel/${request.hotelId}/AddComment`,
            request,
            {
                withCredentials: true,
                formSerializer: {
                    visitor: function (this, value, key) {
                        FormDataSerializer(
                            value,
                            { indices: true, dotsForObjectNotation: true },
                            this as FormData,
                            String(key)
                        );
                        return false;
                    },
                },
                headers: { 'Content-Type': 'multipart/form-data' },
            }
        );
    }

    private get hotelForm(): HotelSearchForm {
        return useHotelForm();
    }

    async loadMore() {
        if (!this.currentPackageSet) {
            return;
        }
        await this.currentPackageSet.loadMore();
    }

    get hasNextPage() {
        return !!this.currentPackageSet?.hasNextPage;
    }

    override get sortByList(): SortBy[] {
        return [
            {
                type: HotelSortBy.MrbilitOffer,
                label: 'پیشنهاد مِستربلیط',
            },
            {
                type: HotelSortBy.HigherPrice,
                label: 'بالاترین قیمت',
            },
            {
                type: HotelSortBy.LowerPrice,
                label: 'کمترین قیمت',
            },
            {
                type: HotelSortBy.HigherScore,
                label: 'بالاترین امتیاز',
            },
        ];
    }

    override get description(): string {
        return this.descriptionTemplate.replace(
            /\((اسم شهر)\)/g,
            this.cityName
        );
    }

    get cityName(): string {
        const { hotelForm } = this;
        return hotelForm.userDestination?.title ?? '';
    }

    override applySort(list: HotelPackage[]): HotelPackage[] {
        const overrideSort = (a: HotelPackage, b: HotelPackage) => {
            if (!this.floatingEnabled) {
                return a.payable && b.payable ? 0 : a.payable ? -1 : 1;
            }
            if (a.isSpecialOffer && b.isSpecialOffer) {
                return 0;
            }
            if (a.isSpecialOffer) {
                return -1;
            }
            if (b.isSpecialOffer) {
                return 1;
            }
            return 0;
        };
        switch (this.sortBy) {
            case HotelSortBy.HigherPrice:
                return list
                    .concat()
                    .sort(
                        (a, b) =>
                            overrideSort(a, b) ||
                            (b.payable ?? 0) - (a.payable ?? 0)
                    );
            case HotelSortBy.LowerPrice:
                return list
                    .concat()
                    .sort(
                        (a, b) =>
                            overrideSort(a, b) ||
                            (a.payable ?? 0) - (b.payable ?? 0)
                    );
            case HotelSortBy.HigherScore:
                return list
                    .concat()
                    .sort(
                        (a, b) =>
                            overrideSort(a, b) ||
                            (b.rating ?? 0) - (a.rating ?? 0)
                    );
            case HotelSortBy.MrbilitOffer:
                // this is the default sorting that server sends
                return list.concat().sort((a, b) => overrideSort(a, b));
            case HotelSortBy.Sale:
            default:
                return list
                    .concat()
                    .sort(
                        (a, b) =>
                            overrideSort(a, b) ||
                            (b.saleCount ?? 0) - (a.saleCount ?? 0)
                    );
        }
    }

    override get sortedReservableList() {
        return this.applySort(this.list);
    }

    override get sortedNonReservableList() {
        return [];
    }

    override get srpImageAlt(): string | null {
        return `رزرو هتل ${this.cityName}`;
    }

    override getInitialFilters(): Record<HotelFilters, any> {
        return {
            [HotelFilters.AccommodationTypes]: null,
            [HotelFilters.Amenities]: null,
            [HotelFilters.Labels]: null,
            [HotelFilters.Price]: null,
            [HotelFilters.Services]: null,
            [HotelFilters.Stars]: null,
        };
    }

    override getPackageSet(
        loaderConfig: HotelPackageLoaderConfig
    ): HotelPackageSet {
        return new HotelPackageSet(loaderConfig, this.getInitialFilters());
    }

    override getItineraryPackageLoaderConfigs(
        params: HotelUrlParams
    ): HotelPackageLoaderConfig[] {
        return [
            {
                adultCount: 1,
                childCount: 0,
                roomCount: 1,
                cityName: params.cityName,
                fromDate: params.fromDate,
                toDate: params.toDate,
                url: params.url,
                static: !!params.static,
            },
        ];
    }

    async initPackageFromPath(params: HotelUrlParams) {
        const { hotelForm } = this;
        hotelForm.setPaxTypeCount({
            type: PaxType.Adult,
            count: params.adultCount || 1,
        });
        hotelForm.setPaxTypeCount({
            type: PaxType.Child,
            count: params.childCount ?? 0,
        });
        hotelForm.setPaxTypeCount({
            type: PaxType.Room,
            count: params.roomCount || 1,
        });
        const searchFormStore = useSearchFormStore();
        searchFormStore.setReserveType(ReserveType.Hotel);
        searchFormStore.urlParams = params;
        try {
            const hotelPackage = await this.loadHotelPackage(params);
            if (hotelPackage) {
                hotelForm.setUserDestinationFromPackage(hotelPackage);
                usePackageSelectStore().removeSelectedPackage(0);
                this.ssrHotelPackage = null;
                if (import.meta.server) {
                    // because of reactivity issue we must save hotel package in ssr hotel package state
                    this.ssrHotelPackage = hotelPackage;
                } else {
                    usePackageSelectStore().setSelectedPackage({
                        package: hotelPackage,
                        index: 0,
                    });
                }
            } else {
                throw new ViewNotFoundError('page not found');
            }
        } catch (error) {
            if ((error as AxiosError).response?.status === 404) {
                throw new ViewNotFoundError('page not found');
            } else {
                throw error;
            }
        }
    }

    async loadHotelPackage(
        params: HotelUrlParams
    ): Promise<HotelPackage | null> {
        try {
            this.isLoadingHotelInfo = true;
            this.currentSelectedPackageId = null;
            const {
                data: { hotel },
            } = await HotelApiApiHotelCityNameHotelNameStaticGet(params);
            this.isLoadingHotelInfo = false;
            if (hotel) {
                return new HotelPackage({
                    hotel,
                    ...params,
                });
            } else {
                return null;
            }
        } finally {
            this.isLoadingHotelInfo = false;
        }
    }

    async loadFilters() {
        await this.currentPackageSet?.loadFilterMetadata();
    }

    async loadHotelPrices() {
        await this.currentPackageSet?.loadPrices();
    }

    override async formAutoReserve(query: LocationQuery) {
        const { hotelForm } = this;
        const params = hotelForm.parsePath(query as HotelPathData);
        await this.initPackageFromPath({
            ...params,
            adultCount: 1,
            childCount: 0,
        });
        await this.loadCurrentSelectedPackageRooms();
        const roomsRegex = /^(.*)-(.*)-(\d+)-(\d+)?$/;
        const packageSelectStore = usePackageSelectStore();
        const selectedPackage = packageSelectStore
            .selectedPackages[0] as HotelPackage;
        const room = !query?.room
            ? []
            : typeof query?.room === 'string'
              ? [query.room]
              : (query.room as string[]);
        const roomsWithCount = room
            ?.map(room => RegExp(roomsRegex).exec(room))
            .filter(r => !!r)
            .map(match => ({
                roomId: match[1],
                priceId: match[2],
                count: +match[3],
                extraBed: +(match[4] ?? 0),
            }));
        const prices = selectedPackage.prices
            .map(p => p.classes)
            .flat()
            .filter(price =>
                roomsWithCount.some(
                    room =>
                        room.priceId === price.id ||
                        room.roomId === price.uniqueId
                )
            );
        if (!prices.length) {
            throw new Error('اتاق مورد نظر یافت نشد.');
        }
        prices.forEach(price => {
            const room = roomsWithCount.find(
                room =>
                    room.priceId === price.id || room.roomId === price.uniqueId
            )!;
            packageSelectStore.selectPriceClassForPackageIndex({
                priceClass: price,
                count: room.count,
                packageIndex: 0,
            });
            if (room.extraBed) {
                packageSelectStore.changeExtraPerson({
                    count: room.extraBed,
                    price,
                });
            }
        });
        const invoiceStore = useInvoiceStore();
        await invoiceStore.loadPreInvoice(selectedPackage);
        const formStore = useFormStore();
        formStore.setItineraryPackagesAndDepartureTime(
            packageSelectStore.selectedPackages
        );
        // set selected rooms to adult count
        hotelForm.setPaxTypeCount({
            type: PaxType.Adult,
            count: selectedPackage.selectedPricesWithPriceClass?.length ?? 0,
        });
    }

    revalidateHotelPackages(ssrHotelPackage: HotelPackage) {
        const packageSelectStore = usePackageSelectStore();
        const today = dayConfig.gregory();
        let fromDate = dayConfig.gregory(ssrHotelPackage.fromDate);
        let toDate = dayConfig.gregory(ssrHotelPackage.toDate);
        if (
            ssrHotelPackage.fromDate &&
            dayConfig.gregory(fromDate).isBefore(today, 'day')
        ) {
            fromDate = today;
        }
        if (ssrHotelPackage.toDate) {
            const dayAfterFromDate = dayConfig.gregory(fromDate).add(1, 'day');
            if (dayConfig.gregory(toDate).isBefore(dayAfterFromDate, 'day')) {
                toDate = dayAfterFromDate;
            }
        }
        packageSelectStore.setSelectedPackage({
            package: new HotelPackage({
                ...ssrHotelPackage,
                fromDate: fromDate.format(formatter()),
                toDate: toDate.format(formatter()),
            }),
            index: 0,
        });
        this.ssrHotelPackage = null;
        return {
            fromDate: fromDate.toDate(),
            toDate: toDate.toDate(),
        };
    }

    async loadFloatingHotelPackage() {
        if (!this.floatingInfo) {
            return;
        }
        const {
            data: { hotel },
        } = await HotelApiApiHotelHotelIdStaticGet({
            hotelId: this.floatingInfo?.hotelId ?? 0,
        });
        const packageSelectStore = usePackageSelectStore();
        const hotelPackage = new HotelPackage({
            hotel,
            fromDate:
                (this.floatingInfo?.reserveItems &&
                    this.floatingInfo?.reserveItems[0].checkin) ??
                '',
            toDate:
                (this.floatingInfo?.reserveItems &&
                    this.floatingInfo?.reserveItems[0].checkout) ??
                '',
            cityName: hotel?.cityEnglishName ?? '',
            hotelName: hotel?.englishTitle ?? '',
        });
        packageSelectStore.setSelectedPackage({
            package: hotelPackage,
            index: 0,
        });
        const roomType =
            this.floatingInfo?.reserveItems?.flatMap(r => {
                if (!r.roomType) {
                    return [];
                }
                const roomSelectedPrice: HotelResponseRoomPrice = {
                    roomId: r.roomTypeId?.toString() ?? '',
                    roomServiceTitle: r.roomType.name ? [r.roomType.name] : [],
                    totalPrice: r.totalPrice ?? 0,
                    payable: r.totalPrice ?? 0,
                    extraBedPrice: r.adultExtraBedPrice ?? 0,
                    extraBedChildPrice: r.childExtraBedPrice ?? 0,
                    extraBedInfantPrice: r.infantExtraBedPrice ?? 0,
                    hasEarlyCheckin: r.hasEarlyCheckin ?? false,
                    earlyCheckinPrice: r.earlyCheckinCustomerPrice ?? 0,
                    hasLateCheckout: r.hasLateCheckout ?? false,
                    lateCheckoutPrice: r.lateCheckoutCustomerPrice ?? 0,
                    // there is no provider in floating mode
                    hotelProvider: null,
                };

                return new HotelRoomType(
                    {
                        ...r.roomType,
                        photo: undefined,
                        capacity: 0,
                        checkoutHour: r.checkin ?? '',
                        checkinHour: r.checkout ?? '',
                        prices: [roomSelectedPrice],
                    },
                    roomSelectedPrice
                );
            }) ?? [];
        packageSelectStore.setCurrentHotelPackagePrices(roomType);
        roomType.forEach(room =>
            packageSelectStore.selectPriceClassForPackageIndex({
                priceClass: room.classes[0],
                count: 1,
                packageIndex: 0,
            })
        );
        const useInvoice = useInvoiceStore();
        const formStore = useFormStore();
        useInvoice.setTitledPax(
            (this.floatingInfo.reserveItems || []).flatMap(
                ({ reserveItemPaxes }) =>
                    reserveItemPaxes?.map(pax => ({
                        ...pax,
                        nationalityCode: pax.nationalId ?? '',
                        nationalCode: pax.nationalId ?? '',
                        type: PaxType.Adult,
                        lastName: pax.lastName ?? '',
                        passportNumber: pax.passportNumber ?? '',
                        nationality: pax.isAlien
                            ? Nationality.Foreigner
                            : Nationality.Iranian,
                        firstName: pax.firstName ?? '',
                        persianFirstName: pax.persianFirstName ?? '',
                        persianLastName: pax.persianLastName ?? '',
                        title: pax.persianFullName ?? '',
                        birthDate: pax.birthDay ?? '',
                        passportExpiry: pax.passportExpiration ?? '',
                        gender: pax.isMan ? Gender.Male : Gender.Female,
                        packageOptionalService: [],
                        carModel: '',
                        carPlate: [],
                        hotelEarlyCheckin: false,
                        hotelLateCheckout: false,
                        packageFreeService: [],
                    })) || []
            )
        );
        useInvoice.setContact({
            mobile:
                (this.floatingInfo.reserveItems &&
                    this.floatingInfo.reserveItems[0]?.reserveItemPaxes &&
                    this.floatingInfo.reserveItems[0]?.reserveItemPaxes[0]
                        ?.mobile) ||
                '',
            email: '',
            landline: '',
            destinationMobile: '',
        });

        useInvoice.informationRegisterStatus =
            InformationRegisterStatus.Registered;
        formStore.setItineraryPackagesAndDepartureTime([hotelPackage]);
        const searchFormStore = useSearchFormStore();
        searchFormStore.setReserveType(
            hotelPackage?.reserveType || formStore.emptyPackage?.reserveType
        );
        formStore.setCounts({
            adultCount: this.floatingInfo.reserveItems?.length || 1,
            childCount: 0,
            infantCount: 0,
        });
        useHotelForm().setUserDestinationFromPackage(hotelPackage);
        await useInvoice.loadPreInvoice(hotelPackage);
        formStore.saveSession();
        useInvoice.saveSession();
    }

    reviveAllPackages(index?: number) {
        const itineraryPackageSet =
            this.itineraryPackageSets?.[index ?? this.currentSetIndex];
        if (itineraryPackageSet) {
            this.itineraryPackageSets[index ?? this.currentSetIndex] =
                HotelPackageSet.revive(itineraryPackageSet);
        }
    }

    async loadNearbyHotels(
        params: HotelSearchApiApiHotelsNearByHotelsGetRequest
    ) {
        this.isLoadingNearbyHotels = true;
        this.nearbyHotels = [];
        const { hotelForm } = this;
        try {
            const { data } =
                await HotelSearchApiApiHotelsNearByHotelsGet(params);
            this.nearbyHotels = data.map(
                value =>
                    new HotelPackage({
                        hotel: value,
                        fromDate: hotelForm.fromDate?.toISOString(),
                        toDate: hotelForm.toDate?.toISOString(),
                    })
            );
        } catch (error) {
            reportException(error);
        } finally {
            this.isLoadingNearbyHotels = false;
        }
    }

    async loadSimilarHotel(
        params: HotelApiApiHotelCityNameHotelNameRecommendationsGetRequest
    ) {
        const { data } =
            await HotelApiApiHotelCityNameHotelNameRecommendationsGet(params);

        return data;
    }

    changeCurrentSelectedPackageInfo(data: {
        fromDate: string;
        toDate: string;
    }) {
        const currentPackage = usePackageSelectStore()
            .selectedPackages[0] as HotelPackage;
        usePackageSelectStore().setSelectedPackage({
            package: new HotelPackage({
                ...currentPackage,
                fromDate: data.fromDate,
                toDate: data.toDate,
            }),
            index: 0,
        });
        this.loadNearbyHotels({
            hotelId: currentPackage.id!,
            from: data.fromDate,
            to: data.toDate,
        });
    }

    override get emptyPackage(): HotelPackage {
        const { hotelForm } = this;
        return new HotelPackage({
            hotel: {
                id: 1,
                title: hotelForm.userDestination?.title,
                hotelTypeName: '',
                englishTitle: '................',
                address: '................',
                rating: 3.4,
                stars: 5,
                hasFreeTransfer: false,
                amenities: [
                    {
                        englishTitle: '...',
                        iconHexCode: 'e994',
                        title: '...',
                    },
                    {
                        englishTitle: '....',
                        iconHexCode: 'e994',
                        title: '.....',
                    },
                    {
                        englishTitle: '....',
                        iconHexCode: 'e994',
                        title: '......',
                    },
                    {
                        englishTitle: '.....',
                        iconHexCode: 'e994',
                        title: '.......',
                    },
                ],
                photos: [],
                shortDescription: '................',
                description: '................',
                termsAndConditions: '................',
                message: null,
                locationImage: '',
                cityId: 79,
                cityName: '',
                checkinHour: '................',
                checkoutHour: '................',
                earlyCheckinHour: '................',
                lateCheckoutHour: '................',
                childAge: 7,
                infantAge: 0,
                latitude: 35.789803,
                longitude: 51.41303,
                comments: [],
                roomTypes: null,
                popularPlaces: [],
                hotelFaqs: [],
                allPhotos: [],
                cancellationRules: null,
            },
            fromDate: hotelForm.fromDate?.toISOString(),
            toDate: hotelForm.toDate?.toISOString(),
        });
    }

    async loadCurrentSelectedPackageRooms() {
        const currentHotelPackage = usePackageSelectStore()
            .selectedPackages[0] as HotelPackage;
        await currentHotelPackage.loadRooms();
    }

    trackLoadRooms(status: SrpEventStatus) {
        const currentHotelPackage = usePackageSelectStore()
            .selectedPackages[0] as HotelPackage;
        if (!currentHotelPackage) {
            return;
        }
        const { hotelForm } = this;
        const authStore = useAuthStore();
        const fromDate = dayConfig
            .gregory(currentHotelPackage.fromDate)
            .set('hour', 12)
            .set('minute', 0)
            .set('second', 0)
            .toDate();

        const toDate = dayConfig
            .gregory(currentHotelPackage.toDate)
            .set('hour', 12)
            .set('minute', 0)
            .set('second', 0)
            .toDate();
        const searchAttributes = {
            reserveType: ReserveType.Hotel,
            reserveTypeFa: this.reserveInfo.title,
            hotel: currentHotelPackage.hotelName,
            fromDate: dayConfig.gregory(fromDate).toDate(),
            toDate: dayConfig.gregory(toDate).toDate(),
            fromDateString: dayConfig.gregory(fromDate).utc(true).toISOString(),
            toDateString: dayConfig.gregory(toDate).utc(true).toISOString(),
            fromDateFa: hotelForm.fromDate
                ? dayConfig.jalali(fromDate).format('dddd YYYY/MM/DD')
                : undefined,
            toDateFa: hotelForm.toDate
                ? dayConfig.jalali(toDate).format('dddd YYYY/MM/DD')
                : undefined,
            nights: dayConfig
                .gregory(toDate)
                .startOf('day')
                .diff(dayConfig.gregory(fromDate).startOf('day'), 'day'),
            link: hotelForm.viewUrl,
            status,
            city: currentHotelPackage.cityName,
            cityTitle: currentHotelPackage.cityTitle,
            creationSource: (authStore.siteSourceId ?? 2).toString(),
            whiteLabel: useSiteConfigs().title,
        };
        useAnalyticsStore().trackAll('srp_hotel_rooms', searchAttributes);
    }

    override get filtersLoading(): boolean {
        return super.filtersLoading || !!this.currentPackageSet?.loadingFilters;
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet || !this.currentPackageSet.filterMetaData) {
            return [];
        }
        const meta = this.currentPackageSet.filterMetaData;
        const accommodationFilterList: IdTitle[] = meta.accommodationTypes.map(
            aco => ({
                id: aco.cumulatedIds.join(),
                title: aco.title ?? '',
            })
        );
        const serviceItems: IdTitle[] = meta.serviceLevels.map(service => ({
            id: service.id?.toString() ?? '',
            title: service.title ?? '',
        }));
        const amenitiesFilterList: IdTitle[] = meta.amenities.map(fa => ({
            id: fa.id?.toString() ?? '',
            title: fa.title ?? '',
        }));

        const labelsFilterList: IdTitle[] =
            this.currentPackageSet.filterMetaData?.specialOffers.map(item => ({
                id: item.id?.toString() ?? '',
                title: item.title ?? '',
            }));
        return this.sortFilters([
            new StarFilter(),
            new PriceFilter(HotelFilters.Price, meta.minPrice, meta.maxPrice),
            new CheckboxFilter(
                HotelFilters.Amenities,
                amenitiesFilterList,
                'امکانات'
            ),
            new CheckboxFilter(
                HotelFilters.Services,
                serviceItems,
                'نوع خدمات وعده غذایی'
            ),
            new CheckboxFilter(
                HotelFilters.AccommodationTypes,
                accommodationFilterList,
                'نوع محل اقامت'
            ),
            new CheckboxFilter(
                HotelFilters.Labels,
                labelsFilterList,
                'برچسب‌ها'
            ),
        ]);
    }

    override applyFilter(
        pkg: HotelPackage,
        filters: Record<HotelFilters, any>
    ): boolean {
        let passed = true;
        if (filters.price) {
            passed &&= (pkg.payable || 0) > filters.price[0];
            passed &&= (pkg.payable || 0) < filters.price[1];
        }
        if (filters.accommodationTypes?.length) {
            passed &&= (filters.accommodationTypes as string[]).some(s =>
                `,${s},`.includes(`,${pkg.accommodationId},`)
            );
        }
        if (filters.amenities?.length) {
            passed &&= (filters.amenities as any[]).every(a =>
                pkg.amenities.some(pa => a.toString() === pa.id.toString())
            );
        }
        if (filters.labels?.length) {
            passed &&= (filters.labels as string[]).some(a =>
                pkg.specialOffers.some(offer => offer.id?.toString() === a)
            );
        }
        if (filters.services?.length) {
            passed &&= (filters.services as string[]).some(
                a => pkg.serviceLevelId.toString() === a.toString()
            );
        }
        if (filters.stars?.length) {
            passed &&= (filters.stars as string[]).some(
                a => pkg.hotelStar.toString() === a.toString()
            );
        }
        return passed;
    }

    get floatingEnabled() {
        return this.currentPackageSet?.floatingEnabled;
    }

    override get mainTitle(): string {
        const { hotelForm } = this;
        return `رزرو هتل ${hotelForm.userDestination?.title}`;
    }
    /** ajax calls **/

    /**
     * get list of inventory of this price class
     *
     * @memberof HotelPriceClass
     */
    async getInventoryPriceList(roomId: string): Promise<HotelPriceObject> {
        const { hotelForm } = this;
        try {
            const { data } =
                await HotelRoomApiApiHotelRoomPriceListingsRoomIdGet({
                    roomId,
                    from: hotelForm.fromDate?.toISOString(),
                });
            const inventoryPriceList = data.priceListings ?? [];
            const price: HotelPriceObject = {};
            inventoryPriceList.forEach(p => {
                if (p.date) {
                    price[dayConfig.gregory(p.date).format(formatter())] = {
                        price: p.price ?? 0,
                        isFull: !p.availableCount,
                    };
                }
            });
            return price;
        } catch (error) {
            reportException(error);
        }
        return {};
    }

    async fetchCouponBlocking(billCode: string) {
        this.hotelCouponBlockingLoading = true;
        try {
            const { data } =
                await ReserveApiApiReserveReserveHotelSuggestionBillCodeGet({
                    billCode,
                });
            this.hotelCouponBlocking = data;
        } catch (e) {
            reportException(e);
        } finally {
            this.hotelCouponBlockingLoading = false;
        }
    }

    async getFloatingState(
        params: ReserveApiApiReserveFloatingBillCodeMacGetRequest
    ) {
        try {
            const { data } =
                await ReserveApiApiReserveFloatingBillCodeMacGet(params);
            this.floatingInfo = data;
        } catch (e) {
            await handleException(e);
            useRouter().push('/');
        }
    }
}
